import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { MembershipTier, TIER_CONFIG, TierConfig } from '@/config/membership';

interface CreditsState {
  creditsRemaining: number;
  creditsUsed: number;
  periodStart: string | null;
  periodEnd: string | null;
}

interface MembershipContextType {
  tier: MembershipTier;
  tierConfig: TierConfig;
  isLoading: boolean;
  subscriptionEnd: string | null;
  isSubscribed: boolean;
  canCreateListing: boolean;
  listingCount: number;
  credits: CreditsState;
  refreshMembership: () => Promise<void>;
  checkCanCreateListing: () => Promise<boolean>;
  refreshCredits: () => Promise<void>;
}

const MembershipContext = createContext<MembershipContextType | undefined>(undefined);

const CACHE_KEY = 'hive_membership_cache';
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

interface CachedMembership {
  tier: MembershipTier;
  subscriptionEnd: string | null;
  listingCount: number;
  timestamp: number;
}

function getCachedMembership(): CachedMembership | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const cached: CachedMembership = JSON.parse(raw);
    if (Date.now() - cached.timestamp < CACHE_TTL) return cached;
    return null;
  } catch {
    return null;
  }
}

function setCachedMembership(data: Omit<CachedMembership, 'timestamp'>) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ ...data, timestamp: Date.now() }));
  } catch {}
}

export const useMembership = () => {
  const context = useContext(MembershipContext);
  if (!context) {
    throw new Error('useMembership must be used within a MembershipProvider');
  }
  return context;
};

interface MembershipProviderProps {
  children: ReactNode;
}

export const MembershipProvider: React.FC<MembershipProviderProps> = ({ children }) => {
  const { user, session } = useAuth();
  const [tier, setTier] = useState<MembershipTier>(() => {
    const cached = getCachedMembership();
    return cached?.tier || 'explorer';
  });
  const [isLoading, setIsLoading] = useState(true);
  const [subscriptionEnd, setSubscriptionEnd] = useState<string | null>(() => {
    const cached = getCachedMembership();
    return cached?.subscriptionEnd || null;
  });
  const [listingCount, setListingCount] = useState(() => {
    const cached = getCachedMembership();
    return cached?.listingCount || 0;
  });
  const [canCreateListing, setCanCreateListing] = useState(true);
  const [credits, setCredits] = useState<CreditsState>({
    creditsRemaining: 0,
    creditsUsed: 0,
    periodStart: null,
    periodEnd: null,
  });
  const lastRefreshRef = useRef<number>(0);

  const refreshCredits = useCallback(async () => {
    if (!user) {
      setCredits({ creditsRemaining: 0, creditsUsed: 0, periodStart: null, periodEnd: null });
      return;
    }

    const { data, error } = await supabase
      .from('match_credits')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) {
      console.error('Error fetching credits:', error);
      return;
    }

    if (data) {
      setCredits({
        creditsRemaining: data.credits_remaining,
        creditsUsed: data.credits_used,
        periodStart: data.period_start,
        periodEnd: data.period_end,
      });
    } else {
      const tierRequests = TIER_CONFIG[tier].monthlyServiceRequests;
      setCredits({ creditsRemaining: tierRequests, creditsUsed: 0, periodStart: null, periodEnd: null });
    }
  }, [user, tier]);

  const refreshMembership = useCallback(async () => {
    if (!user || !session) {
      setTier('explorer');
      setSubscriptionEnd(null);
      setIsLoading(false);
      return;
    }

    // Skip if refreshed recently (within 60s) unless cache was cleared (force refresh)
    const now = Date.now();
    const cacheCleared = !getCachedMembership();
    if (!cacheCleared && now - lastRefreshRef.current < 10000) {
      setIsLoading(false);
      return;
    }
    lastRefreshRef.current = now;

    try {
      // Use cached data for instant display, fetch in background
      const cached = getCachedMembership();
      if (cached) {
        setTier(cached.tier);
        setSubscriptionEnd(cached.subscriptionEnd);
        setListingCount(cached.listingCount);
        const currentTierConfig = TIER_CONFIG[cached.tier];
        setCanCreateListing(cached.listingCount < currentTierConfig.maxListings);
        setIsLoading(false);
      }

      // If we have a fresh cache, skip the expensive listing count queries.
      // Counts only need to be refreshed when the user actually creates/deletes
      // a listing (callers can use checkCanCreateListing() to force a recount).
      const hasFreshCache = !!cached;

      const subResponse = await supabase.functions.invoke('check-subscription', {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });

      let visionsCount = cached?.listingCount ?? 0;
      let servicesCount = 0;
      if (!hasFreshCache) {
        const [v, s] = await Promise.all([
          supabase.from('visions').select('id', { count: 'exact', head: true }).eq('user_id', user.id),
          supabase.from('services').select('id', { count: 'exact', head: true }).eq('user_id', user.id),
        ]);
        visionsCount = v.count || 0;
        servicesCount = s.count || 0;
      }

      let resolvedTier: MembershipTier = 'explorer';

      if (subResponse.error) {
        console.error('Error checking subscription:', subResponse.error);
      } else if (subResponse.data) {
        resolvedTier = (subResponse.data.tier as MembershipTier) || 'explorer';
        setTier(resolvedTier);
        setSubscriptionEnd(subResponse.data.subscription_end || null);
      }

      const totalListings = hasFreshCache ? cached!.listingCount : visionsCount + servicesCount;
      setListingCount(totalListings);
      const currentTierConfig = TIER_CONFIG[resolvedTier];
      setCanCreateListing(totalListings < currentTierConfig.maxListings);

      // Cache the result
      setCachedMembership({
        tier: resolvedTier,
        subscriptionEnd: subResponse.data?.subscription_end || null,
        listingCount: totalListings,
      });
    } catch (err) {
      console.error('Error refreshing membership:', err);
      setTier('explorer');
    } finally {
      setIsLoading(false);
    }
  }, [user, session]);

  const checkCanCreateListing = useCallback(async (): Promise<boolean> => {
    if (!user) return false;

    const [visionsResult, servicesResult] = await Promise.all([
      supabase.from('visions').select('id', { count: 'exact', head: true }).eq('user_id', user.id),
      supabase.from('services').select('id', { count: 'exact', head: true }).eq('user_id', user.id),
    ]);

    const totalListings = (visionsResult.count || 0) + (servicesResult.count || 0);
    const currentTierConfig = TIER_CONFIG[tier];
    const allowed = totalListings < currentTierConfig.maxListings;
    setCanCreateListing(allowed);
    setListingCount(totalListings);
    return allowed;
  }, [user, tier]);

  // Initial load and when user changes - fetch immediately for correct tier
  useEffect(() => {
    if (!user) {
      localStorage.removeItem(CACHE_KEY);
      lastRefreshRef.current = 0;
      setTier('explorer');
      setSubscriptionEnd(null);
      setIsLoading(false);
      return;
    }

    // Use cache immediately for instant display
    const cached = getCachedMembership();
    if (cached) {
      setTier(cached.tier);
      setSubscriptionEnd(cached.subscriptionEnd);
      setListingCount(cached.listingCount);
      setCanCreateListing(cached.listingCount < TIER_CONFIG[cached.tier].maxListings);
      // Don't set isLoading false yet - still verify with server
    }

    // Fetch immediately on every page load / user change
    refreshMembership();
  }, [user?.id, session?.access_token]);

  // Refresh credits when user or tier changes - deferred
  useEffect(() => {
    if (!user) return;
    const timer = setTimeout(() => {
      refreshCredits();
    }, 3000);
    return () => clearTimeout(timer);
  }, [user?.id, tier]);

  // Periodic refresh every 5 minutes (was 60s)
  useEffect(() => {
    if (!user) return;

    const interval = setInterval(() => {
      lastRefreshRef.current = 0; // Force refresh
      refreshMembership();
      refreshCredits();
    }, 300000); // 5 minutes
    return () => clearInterval(interval);
  }, [user, refreshMembership, refreshCredits]);

  const tierConfig = TIER_CONFIG[tier];
  const isSubscribed = tier !== 'explorer';

  return (
    <MembershipContext.Provider
      value={{
        tier, tierConfig, isLoading, subscriptionEnd, isSubscribed,
        canCreateListing, listingCount, credits,
        refreshMembership, checkCanCreateListing, refreshCredits,
      }}
    >
      {children}
    </MembershipContext.Provider>
  );
};
